<script setup lang="ts">
import { LOCALE } from "core/interfaces";
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { translationNormalize } from "~/common/helpers/translation-adapter";
import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateKindByIdDto } from "~/common/interfaces/dto/kinds/update-kind-by-id.dto";
import { useAlertStore } from "~/stores/alert";
import { useKindsStore } from "~/stores/kinds";
import { useModalStore } from "~/stores/modal";

const { t } = useI18n();
const route = useRoute();
const alertStore = useAlertStore();
const kindsStore = useKindsStore();
const { kindsModalEdit } = useModalStore();
const { errors, handleSubmit } = useForm<UpdateKindByIdDto>({
	initialValues: translationNormalize(kindsModalEdit.kind.translations, ["name", "namePlural"]),
	validationSchema: yup.object({
		name: yup.object({
			uk: yup
				.string()
				.optional(),
			en: yup
				.string()
				.optional()
		}),
		namePlural: yup.object({
			uk: yup
				.string()
				.optional(),
			en: yup
				.string()
				.optional()
		})
	})
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: UpdateKindByIdDto) => {
	kindsStore.currentKind = await kindsStore.patchById(kindsModalEdit.kind.id, dto);

	kindsModalEdit.closeModal();

	if (route.name === "components-kinds") {
		kindsStore.getAll({}, kindsStore.recentGetAllQuery);
	}

	alertStore.addAlert({
		title: t("COMPONENTS.KINDS.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.KINDS.MODAL_EDIT.alert.description", { kindName: kindsModalEdit.kind.name }),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: nameUK } = useField<string>("name.uk");
const { value: nameEN } = useField<string>("name.en");
const { value: namePluralUK } = useField<string>("namePlural.uk");
const { value: namePluralEN } = useField<string>("namePlural.en");
</script>

<template>
	<AppModal
		:title="$t('COMPONENTS.KINDS.MODAL_EDIT.title')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="kindsModalEdit.closeModal"
		@submit="onSubmit"
	>
		<span class="font-semibold text-base">
			{{ $t('COMMON.generalInfo') }}
		</span>
		<div class="mt-6">
			<span class="font-semibold text-sm">
				Українською
			</span>
			<div class="mt-2 flex gap-x-2">
				<AppInput
					v-model="nameUK"
					type="text"
					:title="$t('COMMON.name')"
					:placeholder="$t('INPUT.PLACEHOLDER.kind', 1, { locale: LOCALE.UKRAINIAN })"
					:error="errors['name.uk']"
				/>
				<AppInput
					v-model="namePluralUK"
					type="text"
					:title="$t('COMMON.namePlural')"
					:placeholder="$t('INPUT.PLACEHOLDER.kind', 10, { locale: LOCALE.UKRAINIAN })"
					:error="errors['namePlural.uk']"
				/>
			</div>
		</div>
		<div class="mt-4">
			<span class="font-semibold text-sm">
				English
			</span>
			<div class="mt-2 flex gap-x-2">
				<AppInput
					v-model="nameEN"
					type="text"
					:title="$t('COMMON.name')"
					:placeholder="$t('INPUT.PLACEHOLDER.kind', 1, { locale: LOCALE.ENGLISH })"
					:error="errors['name.en']"
				/>
				<AppInput
					v-model="namePluralEN"
					type="text"
					:title="$t('COMMON.namePlural')"
					:placeholder="$t('INPUT.PLACEHOLDER.kind', 10, { locale: LOCALE.ENGLISH })"
					:error="errors['namePlural.en']"
				/>
			</div>
		</div>
	</AppModal>
</template>
